/**
 * Matching for the old-site URL redirects the BackOffice publishes as redirects.json.
 *
 * Lifted out of src/lib/data.ts so the Node server can answer a real 301 before the SPA shell is
 * ever sent: a JavaScript redirect passes no link value to the new address and is invisible to the
 * scrapers that build share cards. The client keeps using the same functions for in-app navigation,
 * so one rule can never mean two different things depending on who resolved it.
 *
 * Plain `.mjs` (typed by the sibling `redirects.d.mts`) so the server imports it as-is.
 */

/** Splits a raw path into the path itself and the query (with its leading "?"), dropping any hash. */
function splitQuery(raw) {
  const s = (raw || '').trim();
  const hash = s.indexOf('#');
  const beforeHash = hash >= 0 ? s.slice(0, hash) : s;
  const q = beforeHash.indexOf('?');
  return q >= 0
    ? { path: beforeHash.slice(0, q), query: beforeHash.slice(q) }
    : { path: beforeHash, query: '' };
}

/** Drops trailing slashes, keeping the original casing. Root stays "/". */
function trimTrailingSlash(path) {
  const p = path.replace(/\/+$/, '');
  return p === '' ? '/' : p;
}

/**
 * Percent-decodes a path so a rule can be written the way the address reads.
 *
 * The editor types "/2025 partnerpakker.pdf", with a space, because that is what the old site's
 * link looked like. The browser sends "/2025%20partnerpakker.pdf". In the app this never showed,
 * because React Router hands over a decoded path — but the server matches against the raw URL, so
 * the rule silently never fired. Both sides decode here instead.
 *
 * A malformed escape (a lone "%") throws rather than decodes; then the path is used as it came,
 * which is still better than matching nothing at all.
 */
function decodePath(path) {
  try {
    return decodeURIComponent(path);
  } catch {
    return path;
  }
}

/** Normalizes a path for matching: decoded, lowercased, no query/hash, no trailing slash. */
function normalizePath(path) {
  return trimTrailingSlash(decodePath(splitQuery(path).path).toLowerCase());
}

/**
 * A wildcard rule matches the prefix itself and everything below it. Two forms, differing only in
 * what happens to the path below the prefix:
 *
 * - "/admin/*" **drops** it — the whole folder lands on the target. This is the default because a
 *   folder is usually redirected to a system that shares none of the old paths, where forwarding
 *   could only produce a 404 on the far side.
 * - "/admin/**" **forwards** it, appended to the target. For a target that mirrors the old
 *   structure.
 *
 * Returns null when the rule is an ordinary exact rule.
 */
function wildcardRule(from) {
  const f = (from || '').trim();
  if (f.endsWith('/**')) return { prefix: normalizePath(f.slice(0, -3)), forwardPath: true };
  if (f.endsWith('/*')) return { prefix: normalizePath(f.slice(0, -2)), forwardPath: false };
  return null;
}

/**
 * Builds the final target: the wildcard remainder is appended to the rule's target, and the
 * incoming query string is carried over — placed before any hash in the target, and joined with
 * "&" when the target already carries a query of its own.
 */
function buildTarget(to, remainder, query) {
  let base = (to || '').trim();
  if (remainder) base = base.replace(/\/+$/, '') + remainder;
  if (!query) return base;

  const hash = base.indexOf('#');
  const beforeHash = hash >= 0 ? base.slice(0, hash) : base;
  const fragment = hash >= 0 ? base.slice(hash) : '';
  const separator = beforeHash.includes('?') ? '&' : '?';
  return beforeHash + separator + query.slice(1) + fragment;
}

/**
 * Resolves a location to the rule that governs it, or null when nothing matches. Pass the query
 * string along with the path ("/app/verify.php?token=x") — it is carried over to the target, so
 * magic links and campaign parameters survive the hop. Pure, so it is unit-testable.
 *
 * Matching is case-insensitive and ignores a trailing slash. A rule may be exact
 * ("/program.php" → "/program") or a wildcard — see {@link wildcardRule} for "/*" versus "/**".
 * Exact rules win over wildcards, and the longest wildcard prefix wins over shorter ones.
 *
 * The result carries `gone`: a rule the editor marked as retired answers 410 Gone instead of
 * moving anyone on, which tells a search engine the address is dead on purpose rather than
 * temporarily missing. `to` is still resolved for those rules, so a 410 can be downgraded to a
 * redirect by unticking the box without touching the target.
 */
export function matchRedirectRule(pathname, rules) {
  const { path, query } = splitQuery(pathname);
  // Decoded, so the rule and the incoming address are compared as the same characters — and so a
  // wildcard's remainder is sliced at the right offset.
  const incoming = trimTrailingSlash(decodePath(path));
  const target = incoming.toLowerCase();

  for (const rule of rules) {
    if (wildcardRule(rule.from) === null && normalizePath(rule.from) === target) {
      return { to: buildTarget(rule.to, '', query), gone: rule.gone === true };
    }
  }

  let best = null;
  for (const rule of rules) {
    const wildcard = wildcardRule(rule.from);
    if (wildcard === null) continue;
    const { prefix } = wildcard;
    const matches = prefix === '/' || target === prefix || target.startsWith(prefix + '/');
    if (matches && (best === null || prefix.length > best.prefix.length)) {
      best = { ...wildcard, to: rule.to, gone: rule.gone === true };
    }
  }
  if (best === null) return null;

  if (!best.forwardPath) return { to: buildTarget(best.to, '', query), gone: best.gone };

  const remainder = best.prefix === '/' ? incoming : incoming.slice(best.prefix.length);
  return { to: buildTarget(best.to, remainder === '/' ? '' : remainder, query), gone: best.gone };
}

/**
 * The target for a location, or null when nothing matches. A retired ("gone") rule returns null:
 * in the browser there is nowhere to send the visitor, so the app shows its 404 page — the server
 * is the one that can answer 410.
 */
export function matchRedirect(pathname, rules) {
  const rule = matchRedirectRule(pathname, rules);
  return rule === null || rule.gone ? null : rule.to;
}

/** Keeps only well-formed rules from parsed JSON, so a bad row cannot break the list. */
export function parseRedirectRules(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter(r => !!r && typeof r.from === 'string' && typeof r.to === 'string')
    .map(r => (r.gone === true ? { from: r.from, to: r.to, gone: true } : { from: r.from, to: r.to }));
}
